import { Injectable, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

import * as fromRoot from '@core/redux';
import * as TaskActions from '@core/redux/task/task.actions';
import { select, Store } from '@ngrx/store';
import * as _ from 'lodash';
import { Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

/**
 * Интерфейс для параметров поискового запроса задач
 */
interface SearchQuery {
  projectId?: string;
  responsibleId?: string[];
  authorId?: string[];
  statusId?: string[];
  tagId?: string[];
  fields?: string[];
  expand?: string[];
  text?: string | null;
  isArchive?: number;
  sort?: string;
}

/**
 * Класс для управления состоянием параметров поиска
 */
class QueryState {
  private _projectId: string | null = null;
  private _text: string | null = null;
  private _fields: string[] = [];
  private _expand: string[] = [];
  private _sort: string = '';
  private _isArchive = 0;
  private _offset = 0;
  private _limit = 20;
  private _authorId: string[] = [];
  private _responsibleId: string[] = [];
  private _statusId: string[] = [];
  private _tagId: string[] = [];

  get projectId(): string | null { return this._projectId; }
  get text(): string | null { return this._text; }
  get fields(): string[] { return this._fields; }
  get expand(): string[] { return this._expand; }
  get sort(): string { return this._sort; }
  get isArchive(): number { return this._isArchive; }
  get offset(): number { return this._offset; }
  get limit(): number { return this._limit; }
  get authorId(): string[] { return this._authorId; }
  get responsibleId(): string[] { return this._responsibleId; }
  get statusId(): string[] { return this._statusId; }
  get tagId(): string[] { return this._tagId; }

  setProjectId(value: string | null): void {
    if (this._projectId !== value) {
      this._projectId = value;
    }
  }

  setText(value: string | null): void {
    if (this._text !== value) {
      this._text = value;
    }
  }

  setSort(value: string): void {
    if (this._sort !== value) {
      this._sort = value;
    }
  }

  setIsArchive(value: number): void {
    if (this._isArchive !== value) {
      this._isArchive = value;
    }
  }

  setLimit(value: number): void {
    if (this._limit !== value) {
      this._limit = value;
    }
  }

  setOffset(value: number): void {
    if (this._offset !== value) {
      this._offset = value;
    }
  }

  setArrayProperty<T extends string[]>(
    target: T,
    value: string[] | string
  ): void {
    const normalized = typeof value === 'string' ? [value] : value;
    if (!_.isEqual(target, normalized)) {
      target.length = 0;
      target.push(...normalized);
    }
  }

  setFields(value: string[] | string): void {
    this.setArrayProperty(this._fields, value);
  }

  setExpand(value: string[] | string): void {
    this.setArrayProperty(this._expand, value);
  }

  setAuthorId(value: string[] | string): void {
    this.setArrayProperty(this._authorId, value);
  }

  setResponsibleId(value: string[] | string): void {
    this.setArrayProperty(this._responsibleId, value);
  }

  setStatusId(value: string[] | string): void {
    this.setArrayProperty(this._statusId, value);
  }

  setTagId(value: string[] | string): void {
    this.setArrayProperty(this._tagId, value);
  }

  /**
   * Переключение ответственного в фильтре
   */
  toggleResponsible(id: string): void {
    const index = this._responsibleId.indexOf(id);
    if (index === -1) {
      this._responsibleId.push(id);
    } else {
      this._responsibleId.splice(index, 1);
    }
  }

  /**
   * Проверка активности ответственного в фильтре
   */
  isActiveResponsible(id: string): boolean {
    return this._responsibleId.indexOf(id) > -1;
  }
}

/**
 * Класс для построения объекта запроса
 */
class QueryBuilder {
  static build(state: QueryState): SearchQuery {
    const query: SearchQuery = {};

    if (state.projectId) {
      query.projectId = state.projectId;
    }

    if (state.responsibleId.length) {
      query.responsibleId = [...state.responsibleId];
    }

    if (state.authorId.length) {
      query.authorId = [...state.authorId];
    }

    if (state.statusId.length) {
      query.statusId = [...state.statusId];
    }

    if (state.tagId.length) {
      query.tagId = [...state.tagId];
    }

    if (state.fields.length) {
      query.fields = [...state.fields];
    }

    if (state.expand.length) {
      query.expand = [...state.expand];
    }

    if (state.text) {
      query.text = state.text;
    }

    if (state.isArchive) {
      query.isArchive = state.isArchive;
    }

    if (state.sort) {
      query.sort = state.sort;
    }

    return query;
  }
}

/**
 * Сервис для управления запросами задач
 * Координирует состояние поиска, навигацию и диспетчеризацию действий
 */
@Injectable({
  providedIn: 'root',
})
export class TaskQueryService implements OnDestroy {
  private readonly store = inject<Store<fromRoot.State>>(Store);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroy$ = new Subject<void>();
  
  private readonly queryState = new QueryState();
  private lastQuery: SearchQuery | null = null;

  // Геттеры для доступа к состоянию
  get responsibleId(): string[] {
    return this.queryState.responsibleId;
  }

  // Сеттеры для обновления состояния
  set tagId(value: string[] | string) {
    this.queryState.setTagId(value);
  }

  set statusId(value: string[] | string) {
    this.queryState.setStatusId(value);
  }

  set responsibleId(value: string[] | string) {
    this.queryState.setResponsibleId(value);
  }

  set authorId(value: string[] | string) {
    this.queryState.setAuthorId(value);
  }

  set limit(value: number) {
    this.queryState.setLimit(value);
  }

  set offset(value: number) {
    this.queryState.setOffset(value);
  }

  set isArchive(value: number) {
    this.queryState.setIsArchive(value);
  }

  set sort(value: string) {
    this.queryState.setSort(value);
  }

  set expand(value: string[] | string) {
    this.queryState.setExpand(value);
  }

  set fields(value: string[] | string) {
    this.queryState.setFields(value);
  }

  set text(value: string | null) {
    this.queryState.setText(value);
  }

  set projectId(value: string | null) {
    this.queryState.setProjectId(value);
  }

  constructor() {
    this.initializeSubscriptions();
  }

  /**
   * Проверяет активность ответственного в фильтре
   */
  isActiveResponsible(id: string): boolean {
    return this.queryState.isActiveResponsible(id);
  }

  /**
   * Переключает ответственного в фильтрации и обновляет навигацию
   */
  toggleResponsible(id: string): void {
    this.queryState.toggleResponsible(id);
    this.navigate();
  }

  /**
   * Обновляет URL с текущими параметрами запроса
   */
  navigate(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.buildQuery(),
    });
  }

  /**
   * Загружает параметры из URL
   */
  load(params: Params): this {
    this.loadParametersFromUrl(params);
    return this;
  }

  /**
   * Принудительно обновляет данные в сторе
   */
  reload(): void {
    this.fetchData(true);
  }

  /**
   * Получает объект запроса для навигации
   */
  getQuery(): SearchQuery {
    return this.buildQuery();
  }

  /**
   * Загружает параметры из URL в состояние
   */
  private loadParametersFromUrl(params: Params): void {
    this.responsibleId = params['responsibleId'] ?? [];
    this.authorId = params['authorId'] ?? [];
    this.statusId = params['statusId'] ?? [];
    this.tagId = params['tagId'] ?? [];
    this.text = params['text'] ?? null;

    const isArchiveParam = params['isArchive'];
    this.isArchive = isArchiveParam == null ? 0 : Number(isArchiveParam) || 0;

    this.sort = params['sort'] ?? this.getDefaultSort();
  }

  /**
   * Возвращает сортировку по умолчанию в зависимости от URL
   */
  private getDefaultSort(): string {
    return this.router.url.indexOf('/load/list') > 0 ? '-updatedAt' : 'board';
  }

  /**
   * Строит объект запроса из текущего состояния
   */
  private buildQuery(): SearchQuery {
    return QueryBuilder.build(this.queryState);
  }

  /**
   * Отправляет запрос на получение данных, если они изменились
   */
  private fetchData(force: boolean = false): void {
    const query = this.buildQuery();
    
    if (!force && _.isEqual(this.lastQuery, query)) {
      return;
    }
    
    this.lastQuery = _.cloneDeep(query);
    this.store.dispatch(new TaskActions.SearchAction(query));
  }

  /**
   * Инициализирует подписки на изменения роутера и проекта
   */
  private initializeSubscriptions(): void {
    const queryParams$ = this.route.queryParams;
    const projectId$ = this.store.pipe(
      select(fromRoot.getProjectSelectedId),
      filter(value => !!value),
      distinctUntilChanged()
    );

    combineLatest([queryParams$, projectId$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([params, projectId]) => {
        this.projectId = projectId;
        this.load(params);
        this.fetchData();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
